import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../../sidebar";
import { MainContainer as AppShell, SideContainer } from "../styles";
import { BackgroundPattern } from "../../ListAreas/styles";
import { guardianAlertService } from "../../../../services/guardianAlertService";
import {
  ALERT_TICKET_ACCEPT_ATTR,
  appendAlertTicketFiles,
  validateAlertTicketSubmission,
} from "../../../../utils/alertTicketAttachments";
import {
  Card,
  Title,
  Subtitle,
  Field,
  Label,
  Select,
  TextArea,
  PhotoRow,
  FileInput,
  RowActions,
  GhostButton,
  PrimaryButton,
  ErrorText,
  ReadOnlyActionValue,
  FileHint,
  SelectedFileName,
} from "./styles";
import { ToolbarWrap, DetailsBody } from "../AlertDetails/styles";
import {
  ButtonRow,
  ButtonContainerLeft,
  StyledButton as GuardianBackButton,
} from "../../ListAreas/styles";

const FIXED_ACTION_SLUG = "respondido_processo_sucessao";
const FIXED_ACTION_LABEL = "Resposta final do campo";

const PROCESS_TYPES = [
  { value: "natural", label: "Processo natural" },
  { value: "antropico", label: "Processo antrópico" },
  { value: "intencional", label: "Processo intencional" },
];

const newPhotoRow = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  file: null,
  processType: "natural",
});

const newFileRow = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  file: null,
});

const FileAttachmentSection = ({
  label,
  hint,
  accept,
  rows,
  onAdd,
  onUpdate,
  onRemove,
  addLabel,
}) => (
  <Field>
    <Label>{label}</Label>
    {hint ? <FileHint>{hint}</FileHint> : null}
    {rows.map((row) => (
      <PhotoRow key={row.key}>
        <FileInput
          type="file"
          accept={accept}
          onChange={(e) =>
            onUpdate(row.key, { file: e.target.files?.[0] || null })
          }
        />
        {row.file ? (
          <SelectedFileName title={row.file.name}>{row.file.name}</SelectedFileName>
        ) : null}
        <GhostButton type="button" onClick={() => onRemove(row.key)}>
          Remover
        </GhostButton>
      </PhotoRow>
    ))}
    <RowActions>
      <GhostButton type="button" onClick={onAdd}>
        {addLabel}
      </GhostButton>
    </RowActions>
  </Field>
);

export const RegisterAlertAction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const collapsed = useSelector((state) => state.sidebar);
  const { alertId, ticketId } = location.state || {};

  const [responses, setResponses] = useState("");
  const [observations, setObservations] = useState("");
  const [photoRows, setPhotoRows] = useState([]);
  const [kmlRows, setKmlRows] = useState([]);
  const [pdfRows, setPdfRows] = useState([]);
  const [archiveRows, setArchiveRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(() => Boolean(alertId), [alertId]);

  const handleBack = () => {
    if (ticketId) {
      navigate("/alert-details", { state: { ticketId } });
      return;
    }
    navigate("/view_alerts");
  };

  const updatePhotoRow = (key, patch) => {
    setPhotoRows((rows) =>
      rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
    );
  };

  const removePhotoRow = (key) => {
    setPhotoRows((rows) => rows.filter((r) => r.key !== key));
  };

  const makeFileRowHandlers = (setRows) => ({
    onAdd: () => setRows((rows) => [...rows, newFileRow()]),
    onUpdate: (key, patch) =>
      setRows((rows) =>
        rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
      ),
    onRemove: (key) => setRows((rows) => rows.filter((r) => r.key !== key)),
  });

  const kmlHandlers = makeFileRowHandlers(setKmlRows);
  const pdfHandlers = makeFileRowHandlers(setPdfRows);
  const archiveHandlers = makeFileRowHandlers(setArchiveRows);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!alertId) {
      setError("Dados do alerta ausentes. Volte aos detalhes e tente novamente.");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const kmlFiles = kmlRows.map((r) => r.file).filter(Boolean);
      const pdfFiles = pdfRows.map((r) => r.file).filter(Boolean);
      const archiveFiles = archiveRows.map((r) => r.file).filter(Boolean);

      validateAlertTicketSubmission({
        photoRows,
        kmlFiles,
        pdfFiles,
        archiveFiles,
      });

      const formData = new FormData();
      formData.append("alert", alertId);
      formData.append("action", FIXED_ACTION_SLUG);
      formData.append(
        "responses",
        responses.trim() ? responses.trim() : "nenhuma"
      );
      formData.append("observations", observations.trim());

      appendAlertTicketFiles(formData, {
        photoRows,
        kmlFiles,
        pdfFiles,
        archiveFiles,
      });

      await guardianAlertService.createAlertTicketMultipart(formData);

      if (ticketId) {
        navigate("/alert-details", { state: { ticketId }, replace: true });
      } else {
        navigate("/view_alerts", { replace: true });
      }
    } catch (err) {
      let msg = "Não foi possível registrar a ação.";
      const data = err?.response?.data;
      if (typeof data === "string") msg = data;
      else if (data?.error) msg = data.error;
      else if (data != null) msg = JSON.stringify(data);
      else if (err?.message) msg = err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!alertId) {
    return (
      <AppShell>
        <BackgroundPattern />
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <DetailsBody>
            <ToolbarWrap>
              <ButtonRow>
                <ButtonContainerLeft>
                  <GuardianBackButton type="button" onClick={handleBack}>
                    Voltar
                  </GuardianBackButton>
                </ButtonContainerLeft>
              </ButtonRow>
            </ToolbarWrap>
            <Card>
              <Title>Dados incompletos</Title>
              <Subtitle>
                Abra esta tela a partir dos detalhes do alerta, usando o botão
                &quot;Registrar Ação Tomada&quot;.
              </Subtitle>
            </Card>
          </DetailsBody>
        </SideContainer>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <DetailsBody>
          <ToolbarWrap>
            <ButtonRow>
              <ButtonContainerLeft>
                <GuardianBackButton type="button" onClick={handleBack}>
                  Voltar
                </GuardianBackButton>
              </ButtonContainerLeft>
            </ButtonRow>
          </ToolbarWrap>

          <Card>
            <Title>Registrar ação tomada</Title>
            <Subtitle>
              Registre, neste campo, as providências adotadas em relação ao alerta identificado. É possível anexar fotografias, arquivos KML/KMZ, PDF e compactados (ZIP/RAR), informando a classificação do processo correspondente a cada foto enviada. Informamos, ainda, que será mantido o registro do usuário responsável pelo cadastramento desta resposta, conforme o login utilizado no sistema.
            </Subtitle>

            <form onSubmit={handleSubmit}>
              {error ? <ErrorText>{error}</ErrorText> : null}

              <Field>
                <Label htmlFor="ticket-action-fixed">Tipo de ação</Label>
                <ReadOnlyActionValue id="ticket-action-fixed" role="status">
                  {FIXED_ACTION_LABEL}
                </ReadOnlyActionValue>
              </Field>

              <Field>
                <Label htmlFor="ticket-responses">Relato / respostas</Label>
                <TextArea
                  id="ticket-responses"
                  value={responses}
                  onChange={(e) => setResponses(e.target.value)}
                  placeholder="Descreva o que foi constatado ou feito no local..."
                />
              </Field>

              <Field>
                <Label htmlFor="ticket-observations">Observações (opcional)</Label>
                <TextArea
                  id="ticket-observations"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Detalhes adicionais..."
                  style={{ minHeight: 80 }}
                />
              </Field>

              <Field>
                <Label>Fotos (opcional)</Label>
                {photoRows.map((row) => (
                  <PhotoRow key={row.key}>
                    <FileInput
                      type="file"
                      accept={ALERT_TICKET_ACCEPT_ATTR.photo}
                      onChange={(e) =>
                        updatePhotoRow(row.key, {
                          file: e.target.files?.[0] || null,
                        })
                      }
                    />
                    {row.file ? (
                      <SelectedFileName title={row.file.name}>
                        {row.file.name}
                      </SelectedFileName>
                    ) : null}
                    <Select
                      value={row.processType}
                      onChange={(e) =>
                        updatePhotoRow(row.key, { processType: e.target.value })
                      }
                      style={{ maxWidth: 220, flex: "0 0 auto" }}
                    >
                      {PROCESS_TYPES.map((pt) => (
                        <option key={pt.value} value={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    </Select>
                    <GhostButton
                      type="button"
                      onClick={() => removePhotoRow(row.key)}
                    >
                      Remover
                    </GhostButton>
                  </PhotoRow>
                ))}
                <RowActions>
                  <GhostButton
                    type="button"
                    onClick={() => setPhotoRows((r) => [...r, newPhotoRow()])}
                  >
                    + Adicionar foto
                  </GhostButton>
                </RowActions>
              </Field>

              <FileAttachmentSection
                label="KML / KMZ (opcional)"
                hint="Arquivos de mapa ou perímetro (.kml, .kmz)"
                accept={ALERT_TICKET_ACCEPT_ATTR.kml}
                rows={kmlRows}
                addLabel="+ Adicionar KML/KMZ"
                {...kmlHandlers}
              />

              <FileAttachmentSection
                label="PDF (opcional)"
                hint="Relatórios ou documentos em PDF"
                accept={ALERT_TICKET_ACCEPT_ATTR.pdf}
                rows={pdfRows}
                addLabel="+ Adicionar PDF"
                {...pdfHandlers}
              />

              <FileAttachmentSection
                label="ZIP / RAR (opcional)"
                hint="Arquivos compactados (.zip, .rar)"
                accept={ALERT_TICKET_ACCEPT_ATTR.archive}
                rows={archiveRows}
                addLabel="+ Adicionar compactado"
                {...archiveHandlers}
              />

              <PrimaryButton type="submit" disabled={!canSubmit || submitting}>
                {submitting ? "Enviando…" : "Enviar registro"}
              </PrimaryButton>
            </form>
          </Card>
        </DetailsBody>
      </SideContainer>
    </AppShell>
  );
};
